import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const studioMinimal: TemplateRegistration = {
  meta: {
    id: 'studio-minimal',
    name: 'Studio Minimal',
    category: 'Minimal',
    description: 'Swiss/Bauhaus inspired grid design with monospace typography and a single red accent',
    previewImage: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
    themeEditable: false,
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(0, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Space Mono', 'IBM Plex Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="sm-reviews">
        <div class="sm-container">
          <div class="sm-review-row">
            <div class="sm-review-stars">${renderStars(Math.round(rating))}</div>
            <p>${rating.toFixed(1)} / 5.0 &mdash; ${reviewCount} reviews</p>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="sm-promo">
        <div class="sm-container">
          <span class="sm-promo-code">${promoDiscount}</span>
          <span class="sm-promo-sep">/</span>
          <span class="sm-promo-title">${promoTitle}</span>
          <span class="sm-promo-desc">${promoDesc}</span>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      // Split about text into 2-3 segments for timeline blocks
      const sentences = safeAbout.split(/(?<=[.!?])\s+/);
      const mid = Math.ceil(sentences.length / 2);
      const block1 = sentences.slice(0, mid).join(' ');
      const block2 = sentences.slice(mid).join(' ');
      sections.push(`
      <section id="about" class="sm-section">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> About</div>
          <div class="sm-about-timeline">
            <div class="sm-about-block">
              <h2>${safeName}</h2>
              <p>${block1}</p>
            </div>
            ${block2 ? `<div class="sm-about-block"><h2>Our Story</h2><p>${block2}</p></div>` : ''}
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="sm-service-item">
            <span class="sm-service-num">${num}</span>
            <div class="sm-service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="sm-service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="sm-section sm-section-ruled">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Services</div>
          <div class="sm-services-scroll">${serviceItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="sm-gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="sm-section">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Gallery</div>
          <div class="sm-gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const items = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="sm-testimonial">
            <span class="sm-testimonial-text">&ldquo;${tContent}&rdquo;</span>
            <span class="sm-testimonial-author">
              <span class="sm-red-dash">&mdash;</span>
              <strong>${tName}</strong>
              ${tRole ? `<span class="sm-testimonial-role">${tRole}</span>` : ''}
            </span>
            ${t.rating ? `<div class="sm-t-stars">${renderStars(t.rating)}</div>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="sm-section sm-section-ruled">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Testimonials</div>
          <div class="sm-testimonials-list">${items}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const faqItems = data.faq.map((f, i) => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="sm-faq-item">
            <div class="sm-faq-num">${num}</div>
            <div class="sm-faq-body">
              <h3>${q}</h3>
              <p>${a}</p>
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="sm-section">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> FAQ</div>
          <div class="sm-faq-list">${faqItems}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="sm-price-row">
            <div class="sm-price-left">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="sm-price-line"></span>
            <span class="sm-price-val">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="sm-section sm-section-ruled">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Pricing</div>
          <div class="sm-price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="sm-section">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Hours</div>
          <ul class="sm-hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="sm-section sm-section-ruled">
        <div class="sm-container">
          <div class="sm-section-label"><span class="sm-red-dot"></span> Contact</div>
          <div class="sm-contact-block">
            <div class="sm-contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="sm-contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="sm-contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="sm-map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Map &rarr;</a>` : ''}
            </div>
            <form class="sm-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Name" required>
              <input type="email" placeholder="Email" required>
              <button type="submit">Submit</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #ffffff; --black: #000000; --red: #dc2626; --gray: #6b7280; --gray-light: #e5e7eb;
      --gray-lighter: #f9fafb; --font-heading: 'Space Mono', monospace; --font-body: 'IBM Plex Sans', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--black); line-height: 1.7; font-size: 16px; }
    .sm-container { max-width: 1080px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--black); text-decoration: none; transition: color .2s; }
    a:hover { color: var(--red); }
    img { max-width: 100%; display: block; }

    /* NAV */
    .sm-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,255,255,.97); backdrop-filter: blur(8px); border-bottom: 1px solid var(--black); }
    .sm-nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1080px; margin: 0 auto; }
    .sm-nav-brand { font-family: var(--font-heading); font-size: .85rem; font-weight: 700; color: var(--black); text-transform: uppercase; letter-spacing: 2px; }
    .sm-nav-links { display: flex; gap: 28px; list-style: none; }
    .sm-nav-links a { font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 1.5px; color: var(--gray); }
    .sm-nav-links a:hover { color: var(--red); }
    .sm-nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .sm-nav-toggle span { display: block; width: 22px; height: 2px; background: var(--black); margin: 5px 0; }

    /* HERO - scroll-snap intro slides */
    .sm-hero { position: relative; height: 100vh; overflow-y: auto; scroll-snap-type: y mandatory; border-bottom: 1px solid var(--black); }
    .sm-hero-rule { position: fixed; top: 0; bottom: 0; left: 24px; width: 4px; background: var(--red); z-index: 2; }
    .sm-hero-slide { height: 100vh; scroll-snap-align: start; display: flex; align-items: center; justify-content: center; position: relative; }
    .sm-hero-slide-inner { max-width: 1080px; margin: 0 auto; padding: 0 24px; width: 100%; }
    .sm-hero-slide h1 { font-family: var(--font-heading); font-size: clamp(2rem, 4.5vw, 3.6rem); font-weight: 700; line-height: 1.15; color: var(--black); text-transform: uppercase; padding-left: 24px; }
    .sm-hero-slide .sm-hero-sub { font-size: 1rem; color: var(--gray); max-width: 520px; margin-bottom: 36px; line-height: 1.7; padding-left: 24px; }
    .sm-hero-cta { display: inline-block; padding: 12px 36px; background: var(--red); color: #fff; font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; border-radius: 0; transition: background .2s; margin-left: 24px; }
    .sm-hero-cta:hover { background: var(--black); color: #fff; }
    .sm-hero-scroll-hint { position: absolute; bottom: 32px; left: 50%; transform: translateX(-50%); font-family: var(--font-heading); font-size: .65rem; text-transform: uppercase; letter-spacing: 2px; color: var(--gray); animation: sm-bounce 2s infinite; }
    @keyframes sm-bounce { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }

    /* SECTIONS */
    .sm-section { padding: 80px 0; }
    .sm-section-ruled { border-top: 1px solid var(--black); }
    .sm-section-label { display: flex; align-items: center; gap: 10px; font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 2px; color: var(--gray); margin-bottom: 40px; }
    .sm-red-dot { width: 8px; height: 8px; background: var(--red); display: inline-block; flex-shrink: 0; }
    .sm-red-dash { color: var(--red); margin-right: 6px; font-weight: 700; }

    /* ABOUT - timeline-style */
    .sm-about-timeline { position: relative; max-width: 700px; margin: 0 auto; padding-left: 40px; }
    .sm-about-timeline::before { content: ''; position: absolute; top: 0; bottom: 0; left: 16px; width: 2px; background: var(--gray-light); }
    .sm-about-block { position: relative; margin-bottom: 40px; }
    .sm-about-block::before { content: ''; position: absolute; left: -32px; top: 6px; width: 12px; height: 12px; background: var(--red); border-radius: 0; }
    .sm-about-block h2 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; color: var(--black); }
    .sm-about-block p { color: var(--gray); line-height: 1.8; font-size: .95rem; }

    /* SERVICES - horizontal scroll-snap cards */
    .sm-services-scroll { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 20px; padding-bottom: 16px; -webkit-overflow-scrolling: touch; }
    .sm-services-scroll::-webkit-scrollbar { height: 4px; }
    .sm-services-scroll::-webkit-scrollbar-track { background: var(--gray-light); }
    .sm-services-scroll::-webkit-scrollbar-thumb { background: var(--red); }
    .sm-service-item { display: flex; flex-direction: column; gap: 12px; padding: 32px 24px; border: 1px solid var(--gray-light); scroll-snap-align: start; min-width: 300px; max-width: 340px; flex-shrink: 0; }
    .sm-service-num { font-family: var(--font-heading); font-size: 2rem; font-weight: 700; color: var(--red); line-height: 1; }
    .sm-service-body h3 { font-family: var(--font-heading); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; color: var(--black); }
    .sm-service-body p { color: var(--gray); font-size: .9rem; line-height: 1.7; }
    .sm-service-price { display: inline-block; margin-top: 10px; font-family: var(--font-heading); font-size: .85rem; color: var(--red); }

    /* GALLERY */
    .sm-gallery-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 2px; }
    .sm-gallery-item img { width: 100%; height: 260px; object-fit: cover; border-radius: 0; transition: opacity .3s; }
    .sm-gallery-item img:hover { opacity: .8; }

    /* TESTIMONIALS - minimal inline */
    .sm-testimonials-list { max-width: 680px; }
    .sm-testimonial { padding: 20px 0; border-bottom: 1px solid var(--gray-light); }
    .sm-testimonial-text { font-size: .95rem; font-style: italic; color: var(--gray); line-height: 1.7; display: inline; }
    .sm-testimonial-author { display: inline; margin-left: 8px; }
    .sm-testimonial-author strong { font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; }
    .sm-testimonial-role { color: var(--gray); font-size: .8rem; margin-left: 4px; }
    .sm-t-stars { margin-top: 6px; }
    .star { color: var(--gray-light); font-size: .9rem; }
    .star.filled { color: var(--red); }

    /* GOOGLE REVIEWS */
    .sm-reviews { padding: 20px 0; border-bottom: 1px solid var(--black); }
    .sm-review-row { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .sm-review-row p { color: var(--gray); font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; }

    /* PROMO */
    .sm-promo { padding: 16px 0; background: var(--black); border-bottom: 1px solid var(--black); }
    .sm-promo .sm-container { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .sm-promo-code { font-family: var(--font-heading); font-weight: 700; color: var(--red); text-transform: uppercase; letter-spacing: 2px; }
    .sm-promo-sep { color: #555; }
    .sm-promo-title { color: #fff; font-family: var(--font-heading); font-size: .8rem; text-transform: uppercase; letter-spacing: 1px; }
    .sm-promo-desc { color: #999; font-size: .82rem; }

    /* FAQ */
    .sm-faq-list { max-width: 680px; }
    .sm-faq-item { display: flex; gap: 20px; padding: 24px 0; border-bottom: 1px solid var(--gray-light); }
    .sm-faq-num { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; color: var(--red); flex-shrink: 0; min-width: 36px; }
    .sm-faq-body h3 { font-family: var(--font-heading); font-size: .82rem; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; color: var(--black); }
    .sm-faq-body p { color: var(--gray); font-size: .92rem; line-height: 1.8; }

    /* PRICE LIST */
    .sm-price-list { max-width: 680px; }
    .sm-price-row { display: flex; align-items: baseline; gap: 16px; padding: 20px 0; border-bottom: 1px solid var(--gray-light); }
    .sm-price-left { flex-shrink: 0; }
    .sm-price-left h3 { font-family: var(--font-heading); font-size: .82rem; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; color: var(--black); }
    .sm-price-left p { color: var(--gray); font-size: .82rem; }
    .sm-price-line { flex: 1; border-bottom: 1px solid var(--gray-light); margin-bottom: 4px; }
    .sm-price-val { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; color: var(--red); white-space: nowrap; }

    /* HOURS */
    .sm-hours-list { list-style: none; max-width: 400px; }
    .sm-hours-list li { padding: 10px 0; border-bottom: 1px solid var(--gray-light); color: var(--gray); font-size: .92rem; font-family: var(--font-body); }

    /* CONTACT - minimal single-line form */
    .sm-contact-block { max-width: 700px; margin: 0 auto; }
    .sm-contact-info { display: flex; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; }
    .sm-contact-item { display: flex; align-items: center; gap: 10px; color: var(--gray); }
    .sm-contact-item .icon-char { color: var(--red); font-size: 18px; line-height: 1; flex-shrink: 0; }
    .sm-contact-item a { color: var(--black); font-weight: 500; }
    .sm-map-link { display: inline-block; margin-top: 8px; font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 1.5px; color: var(--red); }
    .sm-form { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
    .sm-form input { background: var(--bg); border: none; border-bottom: 2px solid var(--black); color: var(--black); padding: 10px 4px; font-family: var(--font-body); font-size: .95rem; border-radius: 0; outline: none; transition: border-color .2s; flex: 1; min-width: 140px; }
    .sm-form input:focus { border-bottom-color: var(--red); }
    .sm-form input::placeholder { color: var(--gray); }
    .sm-form textarea { display: none; }
    .sm-form button { padding: 10px 28px; background: var(--red); color: #fff; border: none; font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; border-radius: 0; cursor: pointer; transition: background .2s; flex-shrink: 0; }
    .sm-form button:hover { background: var(--black); }

    /* FOOTER */
    .sm-footer { padding: 40px 0; border-top: 1px solid var(--black); }
    .sm-footer-inner { display: flex; align-items: center; justify-content: space-between; }
    .sm-footer-brand { font-family: var(--font-heading); font-size: .75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: var(--black); }
    .sm-footer-copy { color: var(--gray); font-size: .78rem; }
    .sm-footer-rule { width: 24px; height: 3px; background: var(--red); }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .sm-nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,255,255,.98); padding: 16px 24px; gap: 16px; border-bottom: 1px solid var(--black); }
      .sm-nav-links.show { display: flex; }
      .sm-nav-toggle { display: block; }
      .sm-hero-slide h1 { font-size: clamp(1.6rem, 4vw, 2.8rem); }
      .sm-form { flex-direction: column; }
      .sm-form input { min-width: 100%; }
      .sm-service-item { min-width: 260px; }
      .sm-footer-inner { flex-direction: column; gap: 12px; text-align: center; }
    }
    @media (max-width: 480px) {
      .sm-hero-rule { left: 12px; width: 3px; }
      .sm-hero-text { padding-left: 12px; }
      .sm-section { padding: 56px 0; }
      .sm-gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .sm-price-row { flex-direction: column; gap: 8px; }
      .sm-price-line { display: none; }
    }
  </style>
</head>
<body>
  <nav class="sm-nav">
    <div class="sm-nav-inner">
      <a href="#" class="sm-nav-brand">${safeName}</a>
      <button class="sm-nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.sm-nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="sm-nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="sm-hero">
    <div class="sm-hero-rule"></div>
    <div class="sm-hero-slide">
      <div class="sm-hero-slide-inner">
        <h1>${safeHeadline}</h1>
      </div>
      <span class="sm-hero-scroll-hint">Scroll</span>
    </div>
    <div class="sm-hero-slide">
      <div class="sm-hero-slide-inner">
        <p class="sm-hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="sm-hero-cta">Contact Us</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="sm-footer">
    <div class="sm-container">
      <div class="sm-footer-inner">
        <span class="sm-footer-brand">${safeName}</span>
        <span class="sm-footer-rule"></span>
        <span class="sm-footer-copy">${safeName}</span>
      </div>
    </div>
  </footer>
</body>
</html>`;
  }
};
